import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink as RouterNavLink } from "react-router-dom";
import { ChevronDown, X } from "lucide-react";
import { cn } from "../../lib/cn";
import type { NavLink, NavMenu } from "./navModel";

interface Section {
  id: string;
  label: string;
  icon?: ReactNode;
  links: NavLink[];
}

export function MobileDrawer({
  open,
  onClose,
  menus,
  extraSections,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  menus: NavMenu[];
  extraSections?: Section[];
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const sections: Section[] = [
    ...menus.map((menu) => ({ id: menu.id, label: menu.label, links: menu.links })),
    ...(extraSections ?? []),
  ];

  return (
    <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      <div className="absolute inset-0 bg-obsidian-700/90 animate-fade-in" onClick={onClose} aria-hidden />
      <aside className="absolute right-0 top-0 flex h-dvh max-h-dvh w-[min(100vw,26rem)] flex-col border-l border-iron-300/60 bg-obsidian-300 shadow-dropdown animate-menu-in">
        <div className="safe-x flex items-center justify-between border-b border-iron-300/50 bg-brass-plate py-4">
          <span className="font-display text-sm uppercase tracking-[0.12em] text-arcane-100">Navigation</span>
          <button onClick={onClose} aria-label="Fermer le menu" className="tap-target grid place-items-center border border-iron-300/50 text-parchment-200/70 hover:text-parchment-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="touch-scroll-y flex-1 overflow-y-auto px-3 py-4">
          {sections.map((section, index) => (
            <div key={section.id} className={cn(index > 0 && "mt-2 border-t border-iron-400/40 pt-2")}>
              <Accordion section={section} onNavigate={onClose} defaultOpen={index === 0} />
            </div>
          ))}
        </nav>

        {footer && <div className="safe-b border-t border-iron-300/50 bg-obsidian-400 p-4">{footer}</div>}
      </aside>
    </div>
  );
}

function Accordion({ section, onNavigate, defaultOpen }: { section: Section; onNavigate: () => void; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const panelId = `mdrawer-${section.id}`;
  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          "flex min-h-11 w-full items-center justify-between gap-2 rounded px-3 py-2.5 text-left transition-colors",
          open ? "bg-arcane-600/20 text-arcane-100" : "text-parchment-100 hover:bg-iron-500/35",
        )}
      >
        <span className="flex items-center gap-2 font-display text-sm uppercase tracking-[0.08em]">
          {section.icon}
          {section.label}
        </span>
        <ChevronDown className={cn("h-4 w-4 opacity-60 transition-transform duration-200", open && "rotate-180")} aria-hidden />
      </button>
      {open && (
        <ul id={panelId} className="mt-1 flex flex-col gap-0.5 pl-2">
          {section.links.map((link) => (
            <li key={link.to}>
              <RouterNavLink
                to={link.to}
                end={link.to === "/" || link.to === "/wiki"}
                onClick={onNavigate}
                className={({ isActive }) => cn(
                  "flex min-h-11 items-center rounded px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-arcane-600/20 font-semibold text-arcane-100 shadow-[inset_3px_0_0_0_#44b6a4]"
                    : "text-parchment-200/75 hover:bg-iron-500/35 hover:text-parchment-100",
                )}
              >
                <span className="min-w-0 truncate">{link.label}</span>
              </RouterNavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DrawerLink({ to, children, onClick }: { to: string; children: ReactNode; onClick?: () => void }) {
  return (
    <Link to={to} onClick={onClick} className="block rounded px-3 py-2 text-sm text-parchment-200/80 hover:bg-iron-500/35 hover:text-parchment-100">
      {children}
    </Link>
  );
}
